// What the Deal sheet knows about the price of the deal being built, so the cart can show the deal
// at once while the server works out the real line. The server's number always replaces it.
//
// The pieces (the same ones the sheet already shows next to each choice):
//   base price of the deal                                  dealData.price
//   per step: the tier upgrade (only for products the tier applies to), the size upgrade, add-ons
//
// Returns null whenever something it needs is missing, and the caller then waits for the server.

const round2 = (n) => Math.round(Number(n) * 100) / 100;
const same = (a, b) => String(a) === String(b);

// The tier upgrade a product incurs: none when the tier lists sub-categories and the product is not in one.
export const tierUpgradeFor = (tier, product) => {
  const base = Number(tier?.upgrade) || 0;
  if (!base) return 0;
  const subCats = tier?.subCats || [];
  if (subCats.length > 0 && !subCats.includes(product?.dishcat)) return 0;
  return base;
};

export const addonUpgradeFor = (recipe, addedQtys) =>
  Object.entries(addedQtys || {}).reduce((sum, [id, qty]) => {
    const ing = recipe?.addons?.find((a) => same(a.id, id));
    return sum + (Number(ing?.price) || 0) * (Number(qty) || 0);
  }, 0);

/**
 * dealData: the deal-data answer ({ label, price, steps, products }); selections: { [stepId]: { productId, sizeId, tierId,
 * addedQtys } }; recipes: { [productId]: recipe | false }.
 * -> { item_name, base_price, total_price, children: [{ name, size, image, upgrade }] } | null
 * (the deal's own line shows base_price and each item shows its upgrade, as the server draws it; total_price is the two added up)
 */
export const dealPlaceholderSpec = (dealData, selections, recipes) => {
  const base = Number(dealData?.price);
  if (!dealData || !Number.isFinite(base) || base < 0) return null;
  const children = [];
  let upgrades = 0;
  for (const step of dealData.steps || []) {
    const sel = selections?.[step.id];
    if (!sel?.productId) continue; // a step left empty (re-opened deal): nothing to show for it
    const tier = (step.tiers || []).find((t) => same(t.id, sel.tierId)) || (step.tiers || [])[0];
    const product = ((dealData.products || {})[tier?.category || tier?.id] || []).find((p) => same(p.id, sel.productId));
    if (!product) return null; // cannot name it: wait for the server
    const size = (step.sizes || []).find((s) => same(s.id, sel.sizeId));
    const upgrade = round2(tierUpgradeFor(tier, product) + (Number(size?.price) || 0) + addonUpgradeFor(recipes?.[sel.productId], sel.addedQtys));
    upgrades += upgrade;
    children.push({ name: product.name, size: size?.label || '', image: product.img || '', upgrade });
  }
  if (children.length === 0) return null;
  return { item_name: dealData.label || 'Deal', base_price: round2(base), total_price: round2(base + upgrades), children };
};
